// src/pages/EventoForm.tsx
import React, { useEffect, useState } from "react";
import { api } from "../services/api";
import Swal from "sweetalert2";
import { Evento } from "./EventoPage";

export interface EventoFormProps {
  evento?: Evento;
  onSave: () => Promise<void>;
  onCancel: () => void;
}

/* -------------------------------
   Funções utilitárias
-------------------------------- */
const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const parseCurrency = (value: string): number => {
  const onlyNumbers = value.replace(/\D/g, "");
  return Number(onlyNumbers) / 100;
};

/* -------------------------------------
   COMPONENTE FORM
-------------------------------------- */
const EventoForm: React.FC<EventoFormProps> = ({ evento, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Evento>({
    dataEvento: new Date().toISOString(),
    dataInicial: new Date().toISOString().slice(0, 16),
    dataFinal: null,
    descricao: "",
    valorInicialCaixa: 0,
    totalReceita: 0,
    totalDespesa: 0,
    observacao: "",
    ativo: true,
    descontoEquipePercentual: 0,
  });

  const [loading, setLoading] = useState(false);

  /* -------------------------------------
      LOAD DO EVENTO
  -------------------------------------- */
  useEffect(() => {
    if (evento) {
      setFormData({
        ...evento,
        dataInicial: new Date(evento.dataInicial!).toISOString().slice(0, 16),
        dataFinal: evento.dataFinal
          ? new Date(evento.dataFinal).toISOString().slice(0, 16)
          : null,
      });
    }
  }, [evento]);

  /* -------------------------------------
      HANDLER GERAL
  -------------------------------------- */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;

    // CAMPOS COM MÁSCARA DE MOEDA
    if (
      ["valorInicialCaixa", "totalDespesa", "totalReceita"].includes(name)
    ) {
      const parsed = parseCurrency(value);
      setFormData((prev) => ({
        ...prev,
        [name]: parsed,
      }));
      return;
    }

    // CAMPOS NORMAIS
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* -------------------------------------
      CONFIRMAR AÇÃO
  -------------------------------------- */
  const confirmarAcao = async (): Promise<boolean> => {
    const resp = await Swal.fire({
      title: formData.id ? "Confirmar atualização" : "Confirmar cadastro",
      text: formData.id
        ? "Deseja realmente atualizar este evento?"
        : "Deseja realmente cadastrar este novo evento?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
    });
    return resp.isConfirmed;
  };

  /* -------------------------------------
      SUBMIT
  -------------------------------------- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // validação: desconto
    if (
      formData.descontoEquipePercentual < 0 ||
      formData.descontoEquipePercentual > 100
    ) {
      return Swal.fire("Erro", "O desconto deve estar entre 0% e 100%.", "error");
    }

    const ok = await confirmarAcao();
    if (!ok) return;

    setLoading(true);

    try {
      const payload = { ...formData, dataEvento: formData.dataEvento };

      if (formData.id) {
        await api.put(`/evento/${formData.id}`, payload);
      } else {
        await api.post(`/evento`, payload);
      }

      Swal.fire("Sucesso!", "Evento salvo com sucesso!", "success");
      await onSave();
    } catch (err: any) {
      Swal.fire("Erro", err.response?.data ?? "Falha ao salvar evento.", "error");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------
      RENDER
  -------------------------------------- */
  return (
    <div className="card p-4 shadow-sm">
      <h5 className="mb-3">
        {formData.id ? "Editar Evento" : "Cadastrar Novo Evento"}
      </h5>

      <form onSubmit={handleSubmit} className="row g-3">

        {/* DESCRIÇÃO */}
        <div className="col-md-6">
          <label className="form-label">Descrição</label>
          <input
            type="text"
            name="descricao"
            value={formData.descricao}
            onChange={handleChange}
            className="form-control"
            required
            disabled={!!formData.id}  // 🔒 bloqueia no modo edição
          />
        </div>

        {/* DATA INICIAL */}
        <div className="col-md-3">
          <label className="form-label">Data Inicial</label>
          <input
            type="datetime-local"
            name="dataInicial"
            value={formData.dataInicial}
            readOnly
            className="form-control"
          />
        </div>

        {/* DATA FINAL */}
        <div className="col-md-3">
          <label className="form-label">Data Final</label>
          <input
            type="datetime-local"
            name="dataFinal"
            value={formData.dataFinal ?? ""}
            onChange={handleChange}
            className="form-control"
            readOnly
          />
        </div>

        {/* VALOR INICIAL (TROCO) */}
        <div className="col-md-4">
          <label className="form-label">Valor Inicial (Troco)</label>
          <input
            type="text"
            name="valorInicialCaixa"
            value={formatCurrency(formData.valorInicialCaixa)}
            onChange={handleChange}
            className="form-control"          
            disabled={!!formData.id}  // 🔒 bloqueia no modo edição  
          />
        </div>

        {/* TOTAL DESPESA */}
        <div className="col-md-4">
          <label className="form-label">Total Despesa</label>
          <input
            type="text"
            name="totalDespesa"
            value={formatCurrency(formData.totalDespesa)}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        {/* TOTAL RECEITA (somente leitura) */}
        <div className="col-md-4">
          <label className="form-label">Total Receita (Recebido)</label>
          <input
            type="text"
            name="totalReceita"
            value={formatCurrency(formData.totalReceita)}
            className="form-control bg-light"
            readOnly
          />
        </div>

        {/* DESCONTO EQUIPE */}
        <div className="col-md-4">
          <label className="form-label">Desconto para Equipe (%)</label>
          <input
            type="number"
            name="descontoEquipePercentual"
            value={formData.descontoEquipePercentual}
            onChange={handleChange}
            className="form-control"
            min={0}
            max={100}
          />
        </div>

        {/* OBSERVAÇÃO */}
        <div className="col-12">
          <label className="form-label">Observação</label>
          <textarea
            name="observacao"
            value={formData.observacao}
            onChange={handleChange}
            className="form-control"
            rows={3}
          />
        </div>

        {/* ATIVO */}
        <div className="col-12 mt-1">
          <input
            type="checkbox"
            name="ativo"
            checked={formData.ativo}
            onChange={handleChange}
            className="form-check-input me-2"
            disabled
          />
          <label className="form-check-label">Evento ativo</label>
        </div>

        {/* BOTÕES */}
        <div className="col-12 d-flex justify-content-end gap-2 mt-4">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading
              ? "Salvando..."
              : formData.id
              ? "Atualizar"
              : "Salvar"}
          </button>
        </div>

      </form>
    </div>
  );
};

export default EventoForm;
